import {BuildingClient} from "../../client/BuildingClient";
import {MeterClient} from "../../client/MeterClient";
import {Building} from "../BuildingStatus";
import {NavigateFunction} from "react-router-dom";
import {AsyncComponent} from "../common/AsyncComponent";
import {CrudTable} from "../common/CrudTable";
import {addProgramme, createTariff, editControlStrategy, editProgramme, editTariff} from "../../Routes";
import {Alert} from "react-bootstrap";
import React from "react";
import {useEntitlements} from "../../EntitlementProvider";
import {Permission} from "../../Permission";
import {GridColDef} from "@mui/x-data-grid";

export interface BuildingPropertiesProps {
    buildingClient: BuildingClient
    meterClient: MeterClient
    selectedBuilding: Building
    navigate: NavigateFunction
    disableVirtualizationForTesting?: boolean
}


export const BuildingProperties =({buildingClient, meterClient, selectedBuilding, navigate, disableVirtualizationForTesting}: BuildingPropertiesProps) => {
    const entitlements = useEntitlements()

    return selectedBuilding ? <div data-testid='metering-page-building-properties-section'>
        <AsyncTariffs buildingClient={buildingClient} selectedBuilding={selectedBuilding} navigate={navigate}/>
        <AsyncProgrammes buildingClient={buildingClient} selectedBuilding={selectedBuilding} navigate={navigate} disableVirtualizationForTesting={disableVirtualizationForTesting}/>
        <AsyncControlStrategy buildingClient={buildingClient} selectedBuilding={selectedBuilding} navigate={navigate}/>
        {entitlements.has(Permission.READ_BUILDING) && entitlements.has(Permission.READ_DEVICE) && <AsyncMeters meterClient={meterClient} selectedBuilding={selectedBuilding} disableVirtualizationForTesting={disableVirtualizationForTesting}/>}
    </div> : <></>
}

export interface BaseAsyncProps {
    buildingClient: BuildingClient
    selectedBuilding: Building
    navigate: NavigateFunction
    disableVirtualizationForTesting?: boolean
}

const AsyncTariffs = ({buildingClient, selectedBuilding, navigate}: BaseAsyncProps)=>  {
    const loadTariffs = async (building: Building) =>
        (await buildingClient.retrieveTariffs(building.id))
            .onSuccess(tariffs => tariffs.map(
                (tariff, i) => ({
                    ...tariff,
                    queryableDataId: tariff.id.toString(),
                    id: i,
                    startDate: tariff.startDate.toISODate(),
                    endDate: tariff.endDate.toISODate(),
                }))).unwrap([])

    return <AsyncComponent loaderFunction={()=> loadTariffs(selectedBuilding)}
                           dataPropName="initialData" effectDependency={selectedBuilding.id}>
        <CrudTable initialData={[]}
                   title={'Tariffs'}
                   editRowById={tariff => Promise.resolve(navigate(editTariff.withParams([selectedBuilding.id, Number(tariff)])))}
                   create={() => navigate(createTariff.withParams(selectedBuilding.id))}
                   testId="tariffTable"
        /></AsyncComponent>
}

export interface AsyncMetersProps {
    meterClient: MeterClient
    selectedBuilding: Building
    disableVirtualizationForTesting?: boolean
}

const AsyncMeters = ({meterClient, selectedBuilding, disableVirtualizationForTesting}: AsyncMetersProps)=>  {
    const loadMeters = async (building: Building) => (await meterClient.retrieveMetersWithDevices(building.id))
        .onSuccess(meters => meters.map(m => ({
            queryableDataId: m.id,
            mpan: m.mpan,
            deviceId: m.deviceId ?? '',
            imageMeterId: m.imageMeterId ?? '',
        })))
        .unwrap([])

    return <AsyncComponent loaderFunction={()=> loadMeters(selectedBuilding)}
                           dataPropName="initialData" effectDependency={selectedBuilding.id}>
        <CrudTable initialData={[]}
                   title={'Meters'}
                   testId="meterTable"
                   disableVirtualizationForTesting={disableVirtualizationForTesting}
        /></AsyncComponent>
}

const AsyncControlStrategy = ({buildingClient, selectedBuilding, navigate}: BaseAsyncProps)=>  {

    const loadControlStrategy = (id: number) => async () =>
        (await buildingClient.retrieveBuildingStrategies(id))
            .onSuccess((css => css.map((cs, index) => ({...cs, operatingHoursStart: cs.operatingHoursStart.value, operatingHoursEnd: cs.operatingHoursEnd.value, queryableDataId: cs.id, id: index}))))
            .unwrap([])

    const subtitle = (id: number) => (id === 1 || id === 5 ) ? <Alert variant='warning'>Adjusted Strategy - Talk to Ops for Details</Alert>  : <></>

    const columns: GridColDef[] = [
            {
                "field": "bmsStrategyId",
                "headerClassName": "table-column-header",
                "headerName": "bmsStrategyId",
            },
            {
                "field": "saving",
                "headerClassName": "table-column-header",
                "headerName": "saving",
            },
            {
                "field": "avoidExtremeWeather",
                "headerClassName": "table-column-header",
                "headerName": "avoidExtremeWeather",
            },
            {
                "field": "autoSchedulingEnabled",
                "headerClassName": "table-column-header",
                "headerName": "autoSchedulingEnabled",
            },
            {
                "field": "queryableDataId",
                "headerClassName": "table-column-header",
                "headerName": "queryableDataId",
            }
        ]

    return <AsyncComponent loaderFunction={loadControlStrategy(selectedBuilding.id)} dataPropName="initialData"
                           effectDependency={selectedBuilding.id}>
        <CrudTable initialData={[]}
                   title={'Control Strategy'}
                   subtitle={subtitle(selectedBuilding.id)}
                   testId="strategyTable"
                   editRowById={controlStrategy => Promise.resolve(navigate(editControlStrategy.withParams([selectedBuilding.id, Number(controlStrategy)])))}
                   customColumns={columns}
                   initialSortModel={[{field: 'bmsStrategyId', sort: 'asc'}]}
        /></AsyncComponent>
}


const AsyncProgrammes = ({buildingClient, selectedBuilding, navigate, disableVirtualizationForTesting}: BaseAsyncProps)=>  {

    const loadProgrammes: (id: Building) => () => Promise<{
        queryableDataId: number;
        name: string;
        minimumPriceInEurosPerMegawattHour: number | undefined
        participatesInCapacityBidding: boolean | undefined
    }[]> = (id: Building) => async () =>
        (await buildingClient.retrieveBuildingProgrammes(id.id))
            .onSuccess((p => p.map((value, index) => {
                return {
                    queryableDataId: index,
                    name: value.name,
                    minimumPriceInEurosPerMegawattHour: value.minimumPriceInEurosPerMegawattHour,
                    participatesInCapacityBidding: value.participatesInCapacityBidding
                };
            })))
            .unwrap([])

    return <AsyncComponent loaderFunction={loadProgrammes(selectedBuilding)} dataPropName="initialData"
                           effectDependency={selectedBuilding.id}>
        <CrudTable<{
            queryableDataId: string;
            name: string;
            minimumPriceInEurosPerMegawattHour: number | undefined;
            participatesInCapacityBidding: boolean | undefined;
        }> initialData={[]}
                   title={'Programmes'}
                   testId="programmeTable"
                   disableVirtualizationForTesting={disableVirtualizationForTesting}
                   create={() => navigate(addProgramme.withParams(selectedBuilding.id))}
                   editRowBySelectedData={(selectedData) =>  Promise.resolve(
                       navigate(editProgramme.withParams({buildingId: selectedBuilding.id.toString(), programmeName: selectedData.name})))}
        />
    </AsyncComponent>
}
